import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { formatCurrency } from '@/Utils/helpers';

/**
 * Composable for currency formatting based on current company
 *
 * Usage:
 * const { currency, currencySymbol, currencyCode, formatMoney } = useCurrency();
 *
 * // Format amount with company currency
 * formatMoney(100.50) // Returns "$ 100.50" or "€ 100,50" etc.
 */
export function useCurrency() {
    const page = usePage();

    // Get current company from page props
    const company = computed(() => page.props.auth?.company || page.props.company || null);

    // Get currency object - handle nested {data: {...}} wrapper from Laravel Resources
    const currency = computed(() => {
        const currencyData = company.value?.currency;
        if (!currencyData) return null;

        // If currency is wrapped in a data object, unwrap it
        return currencyData.data || currencyData;
    });

    // Get currency symbol
    const currencySymbol = computed(() => currency.value?.symbol || '$');

    // Get currency code
    const currencyCode = computed(() => currency.value?.code || 'USD');

    // Get decimal places
    const decimalPlaces = computed(() => currency.value?.decimal_places || 2);

    /**
     * Format an amount with the company's currency
     * @param {number} amount - Amount to format
     * @returns {string} Formatted currency string
     */
    const formatMoney = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return formatCurrency(amount, company.value);
    };

    return {
        company,
        currency,
        currencySymbol,
        currencyCode,
        decimalPlaces,
        formatMoney,
    };
}
